import clsx from 'clsx'
import { Ban, Cpu, Sparkles, TriangleAlert, XCircle } from 'lucide-react'
import type { FitTier } from '@/services/types'

interface Props {
  tier: FitTier
  reason: string | null
  onGpu?: boolean
}

/**
 * Says whether a model suits this machine, and why.
 *
 * The reason is the point. A bare "Too large" invites the user to argue with
 * it; "Needs about 4.1 GB while running, only 2.3 GB free" tells them what
 * would have to change.
 */
export function ModelFitBadge({ tier, reason, onGpu }: Props) {
  // A tier this build does not know is treated as no answer rather than as an
  // error. The server decides the tiers and may add one; a bare lookup would
  // then hand `undefined` to the markup below and take the whole models list
  // down to a white screen over a badge. The rest of the app reads unknown
  // server values the same way -- see `ModelCategory.of` and `LocalEngine.of`,
  // which skip what they do not recognise.
  const style = tier === 'UNKNOWN' ? undefined : TIERS[tier]
  if (!style) return null

  return (
    <span
      title={reason ?? undefined}
      className={clsx(
        'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
        style.className,
      )}
    >
      <style.icon size={10} />
      {onGpu && tier !== 'TOO_LARGE' ? `${style.label} · GPU` : style.label}
    </span>
  )
}

const TIERS = {
  RECOMMENDED: {
    label: 'Recommended',
    icon: Sparkles,
    className: 'bg-accent/15 text-accent',
  },
  WORKS: {
    label: 'Runs well',
    icon: Cpu,
    className: 'bg-success/15 text-success',
  },
  TIGHT: {
    label: 'Tight fit',
    icon: TriangleAlert,
    className: 'bg-warning/15 text-warning',
  },
  TOO_LARGE: {
    label: 'Too large',
    icon: XCircle,
    className: 'bg-danger/10 text-danger',
  },
  // Deliberately not "Too large". The machine may have memory to spare and
  // still be the wrong shape -- a video model on a box with no graphics card
  // -- and calling that a size problem invites the user to go looking for
  // memory to free. The reason carries the specifics.
  UNSUPPORTED: {
    label: 'Not supported',
    icon: Ban,
    className: 'bg-danger/10 text-danger',
  },
  UNKNOWN: {
    label: '',
    icon: Cpu,
    className: '',
  },
} as const
